'use client';
import './sort-tile.css';
import { useRef, useState } from 'react';
import { Box, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { PartialRing } from '@/components/PartialRing';
import { getThumbUrl } from '@/lib/imageUrl';

// Tel-tegel voor de sort mode: hetzelfde gebaren-model als de tegel op de
// overzichtspagina (tap = ±1, long-press = alles/reset, Enter/Space idem met
// Shift), maar zonder de admin- en detail-ballast - puur tellen.
export function SortPartTile({ part, bagId, checkedCount = 0, inFoundSection = false, onToggleCheck, isHighlighted = false }) {
  const partKey = `${bagId}_${part.partId}_${part.color_id || 'nocolor'}`;
  const [isPressed, setIsPressed] = useState(false);

  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const touchStartPos = useRef(null);

  const handleClick = () => {
    if (longPressTriggered.current) return;
    onToggleCheck(partKey, part.quantity, inFoundSection ? 'decrement' : 'increment');
  };

  const handleLongPressStart = (e) => {
    const touch = e?.touches?.[0];
    touchStartPos.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      if (inFoundSection) {
        onToggleCheck(partKey, part.quantity, 'reset');
      } else {
        onToggleCheck(partKey, part.quantity, 'complete', part);
      }
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setTimeout(() => { longPressTriggered.current = false; }, 0);
  };

  // Scrollen met een duim op een tegel mag nooit de long-press triggeren
  const handleTouchMove = (e) => {
    if (!longPressTimer.current || !touchStartPos.current) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartPos.current.x;
    const dy = touch.clientY - touchStartPos.current.y;
    if (dx * dx + dy * dy > 100) {
      setIsPressed(false);
      handleLongPressEnd();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (e.shiftKey) {
      onToggleCheck(partKey, part.quantity, inFoundSection ? 'reset' : 'complete', part);
    } else {
      onToggleCheck(partKey, part.quantity, inFoundSection ? 'decrement' : 'increment');
    }
  };

  const isChecked = checkedCount >= part.quantity;
  const isPartial = checkedCount > 0 && !isChecked;
  const remaining = Math.max(0, part.quantity - checkedCount);

  return (
    <div
      className={isHighlighted ? 'sort-tile part-tile-highlight' : 'sort-tile'}
      data-part-key={`${part.partId}_${part.color_id || 'nocolor'}`}
      data-found={inFoundSection || undefined}
      role="button"
      tabIndex={0}
      aria-label={
        `${part.name || `Part ${part.partId}`}${part.colorName ? `, ${part.colorName}` : ''}: ${Math.min(checkedCount, part.quantity)} of ${part.quantity} found. ` +
        (inFoundSection
          ? 'Enter: put one back. Shift plus Enter: reset this part.'
          : 'Enter: mark one found. Shift plus Enter: mark all found.')
      }
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={() => { setIsPressed(true); handleLongPressStart(); }}
      onMouseUp={() => { setIsPressed(false); handleLongPressEnd(); }}
      onMouseLeave={() => { setIsPressed(false); handleLongPressEnd(); }}
      onTouchStart={(e) => { setIsPressed(true); handleLongPressStart(e); }}
      onTouchMove={handleTouchMove}
      onTouchEnd={() => { setIsPressed(false); handleLongPressEnd(); }}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        // Effen tinten: de tegel moet een dichte lichte kaart blijven, anders
        // schijnt de grijze stage-achtergrond erdoorheen (multiply-blend)
        backgroundColor: isPressed
          ? (inFoundSection ? '#d3f2df' : '#d0e7fb')
          : undefined,
      }}
    >
      {isChecked && (
        <Box
          style={{
            position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%',
            backgroundColor: '#40c057', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          <IconCheck size={13} color="white" stroke={3} />
        </Box>
      )}
      {isPartial && (
        <PartialRing fraction={part.quantity > 0 ? checkedCount / part.quantity : 0} />
      )}

      <img
        src={getThumbUrl(part.imageUrl, 192)}
        alt=""
        decoding="async"
        loading="lazy"
        style={{
          display: 'block', maxWidth: 92, height: 'auto', mixBlendMode: 'multiply',
          filter: isChecked ? 'grayscale(50%) opacity(0.7)' : 'none',
          transition: 'filter 0.15s ease',
        }}
      />

      <div style={{ textAlign: 'center', opacity: isChecked ? 0.6 : 1 }}>
        <Text fw={700} size="sm" component="div">
          {checkedCount > 0 ? (
            inFoundSection
              ? <>{Math.min(checkedCount, part.quantity)}x <Text component="span" size="xs" c="dimmed" fw={400}>/ {part.quantity}</Text></>
              : <>{remaining}x <Text component="span" size="xs" c="dimmed" fw={400}>/ {part.quantity}</Text></>
          ) : (
            <>{part.quantity}x</>
          )}
        </Text>
        {part.name && (
          <Text size="xs" fw={500} lineClamp={2} component="div" style={{ color: '#495057', lineHeight: 1.25, maxWidth: 104 }}>
            {part.name}
          </Text>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {part.colorHex && (
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
              backgroundColor: `#${String(part.colorHex).replace(/^#/, '')}`,
              border: '1px solid rgba(0, 0, 0, 0.15)',
            }} />
          )}
          <Text c="dimmed" size="xs" component="span" truncate style={{ maxWidth: 92 }}>
            {part.colorName || part.partId}
          </Text>
        </div>
      </div>
    </div>
  );
}
